/**
 * StoryCard Component
 * 
 * Displays a user story in a compact card format with quick actions.
 * Can be expanded to edit or view full details.
 */

import React from 'react';
import { UserStory } from '../types/story';
import { getStoryIdForDisplay } from '../utils/storyIds';
import { formatPriorityTag } from '../types/story';

interface StoryCardProps {
  story: UserStory;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

const priorityColors = {
  Alta: 'bg-red-100 text-red-800 border-red-300',
  Media: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Baja: 'bg-green-100 text-green-800 border-green-300',
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-600',
  review: 'bg-blue-100 text-blue-600',
  approved: 'bg-green-100 text-green-600',
};

const statusLabels = {
  draft: 'Borrador',
  review: 'En Revisión',
  approved: 'Aprobada',
};

export const StoryCard: React.FC<StoryCardProps> = ({
  story,
  index,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  const storyIdLabel = getStoryIdForDisplay(story, index);
  const priorityTag = formatPriorityTag(story.priority_sequence);
  const priorityDisplay = priorityTag
    ? (story.priority === 'Media' ? priorityTag : `${story.priority} ${priorityTag}`)
    : story.priority;

  return (
    <div 
      className={`bg-white rounded-lg shadow-md border-l-4 transition-all duration-200 ${
        story.priority === 'Alta' ? 'border-l-red-500' :
        story.priority === 'Media' ? 'border-l-yellow-500' :
        'border-l-green-500'
      } ${isExpanded ? 'ring-2 ring-blue-300' : 'hover:shadow-lg'}`}
    >
      {/* Card Header */}
      <div 
        className="p-4 cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-mono text-gray-500">
                {storyIdLabel}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[story.priority]}`}>
                {priorityDisplay}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[story.status]}`}>
                {statusLabels[story.status]}
              </span>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                {story.story_points} pts
              </span>
            </div>
            <h4 className="font-semibold text-gray-900 text-lg">
              {story.title || 'Sin título'}
            </h4>
            <p className="text-gray-600 text-sm mt-1 line-clamp-2">
              {story.user_story || 'Sin descripción'}
            </p>
          </div>
          <button
            className="ml-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
          >
            <svg 
              className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Tags row */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="text-xs text-gray-500">Rol:</span>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
            {story.rol || 'Sin asignar'}
          </span>
          {story.epic && (
            <>
              <span className="text-xs text-gray-500">Épica:</span>
              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                {story.epic}
              </span>
            </>
          )}
          <span className="text-xs text-gray-500">Criterios:</span>
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
            F: {story.criterios_funcionales.length} | T: {story.criterios_tecnicos.length}
          </span>
          <span className="text-xs text-gray-500">Tareas:</span>
          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
            {story.tasks.length}
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {/* User Story */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">Historia de Usuario</h5>
            <p className="text-gray-600 bg-white p-3 rounded border border-gray-200">
              {story.user_story || 'Como [rol], quiero [acción], para [beneficio]'}
            </p>
          </div>

          {/* Acceptance Criteria */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">
              Criterios Funcionales ({story.criterios_funcionales.length})
            </h5>
            {story.criterios_funcionales.length > 0 ? (
              <ul className="space-y-1">
                {story.criterios_funcionales.map((criterio, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    {criterio}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 italic">Sin criterios definidos</p>
            )}
          </div>

          <div className="mb-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">
              Criterios Técnicos ({story.criterios_tecnicos.length})
            </h5>
            {story.criterios_tecnicos.length > 0 ? (
              <ul className="space-y-1">
                {story.criterios_tecnicos.map((criterio, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-600">
                    <span className="text-blue-500 mr-2">⚙️</span>
                    {criterio}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 italic">Sin criterios técnicos definidos</p>
            )}
          </div>

          {/* Standard Tasks */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">
              Tareas Estándar ({story.tasks.length})
            </h5>
            <div className="grid grid-cols-2 gap-2">
              {story.tasks.map((task, idx) => (
                <div 
                  key={idx}
                  className="text-xs p-2 bg-white rounded border border-gray-200"
                >
                  <span className="font-medium text-gray-700">{task.title}</span>
                  <span className="text-gray-400 ml-2">({task.estimated_hours}h)</span>
                </div>
              ))}
            </div>
          </div>

          {/* Resources */}
          {(story.figma_links.length > 0 || story.insumos.length > 0) && (
            <div className="mb-4">
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Recursos</h5>
              <div className="flex flex-wrap gap-2">
                {story.figma_links.map((link, idx) => (
                  <a 
                    key={`figma-${idx}`}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-2 py-1 bg-pink-100 text-pink-700 rounded hover:bg-pink-200 transition-colors"
                  >
                    🎨 Figma #{idx + 1}
                  </a>
                ))}
                {story.insumos.map((insumo, idx) => (
                  <span 
                    key={`insumo-${idx}`}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                  >
                    📎 {insumo}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
            >
              📋 Duplicar
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
            >
              🗑️ Eliminar
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium"
            >
              ✏️ Editar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
